import { Customer, getCustomersPaged } from '../customers';

const PAGE_SIZE = 15;

type SelectCustomerCacheEntry = {
  businessId: string;
  rows: Customer[];
  total: number;
  fetchedAt: number;
  promise: Promise<{ rows: Customer[]; total: number }> | null;
};

let cache: SelectCustomerCacheEntry | null = null;

export const SELECT_CUSTOMER_PAGE_SIZE = PAGE_SIZE;

export function peekSelectCustomerCache(businessId: string): {
  rows: Customer[];
  total: number;
} | null {
  if (!businessId || cache?.businessId !== businessId || cache.rows.length === 0) {
    return null;
  }
  return { rows: cache.rows, total: cache.total };
}

export function invalidateSelectCustomerCache(businessId?: string) {
  if (!businessId || cache?.businessId === businessId) {
    cache = null;
  }
}

/** Warm the first page so Select Customer can open without waiting. */
export function prefetchSelectCustomers(businessId: string): Promise<{
  rows: Customer[];
  total: number;
}> {
  if (!businessId) return Promise.resolve({ rows: [], total: 0 });

  if (cache?.businessId === businessId) {
    if (cache.promise) return cache.promise;
    if (cache.rows.length > 0) {
      return Promise.resolve({ rows: cache.rows, total: cache.total });
    }
  }

  const promise = getCustomersPaged(businessId, {
    page: 1,
    pageSize: PAGE_SIZE,
  })
    .then(({ rows, total }) => {
      cache = {
        businessId,
        rows,
        total,
        fetchedAt: Date.now(),
        promise: null,
      };
      return { rows, total };
    })
    .catch((err) => {
      if (cache?.businessId === businessId) {
        cache.promise = null;
      }
      throw err;
    });

  cache = {
    businessId,
    rows: cache?.businessId === businessId ? cache.rows : [],
    total: cache?.businessId === businessId ? cache.total : 0,
    fetchedAt: cache?.businessId === businessId ? cache.fetchedAt : 0,
    promise,
  };

  return promise;
}

export function rememberSelectCustomerPage(
  businessId: string,
  rows: Customer[],
  total: number
) {
  if (!businessId) return;
  cache = {
    businessId,
    rows,
    total,
    fetchedAt: Date.now(),
    promise: null,
  };
}
